import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import '../../styles/Dashboard.css';

// Custom hook for child progress - simplified version of mobile version
const useChildProgress = (childId) => {
  const [stats, setStats] = useState({
    assignmentCompletion: 0,
    assessmentAverage: { value: 0, text: 'N/A' },
    attendanceRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!childId) {
      setLoading(false);
      return;
    }

    const fetchProgress = async () => {
      try {
        // Use existing parent-specific API endpoints
        const [assignmentsResponse, attendanceResponse] = await Promise.allSettled([
          api.get(`/parents/children/${childId}/assignments`),
          api.get(`/parents/children/${childId}/attendance`)
        ]);

        // Process assignments data
        if (assignmentsResponse.status === 'fulfilled') {
          const assignmentsData = assignmentsResponse.value.data.data || [];
          
          // Calculate metrics from assignments
          const totalAssignments = assignmentsData.length;
          const completedAssignments = assignmentsData.filter(assignment => 
            assignment.status === 'completed' &&
            (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
          ).length;
          
          const completionRate = totalAssignments > 0 
            ? Math.round((completedAssignments / totalAssignments) * 100) 
            : 0;
          
          // Calculate assessment average
          const assessedAssignments = assignmentsData.filter(assignment => 
            assignment.assessment && assignment.assessment.rating
          );
          
          let assessmentAvgValue = 0;
          let assessmentText = 'N/A';
          
          if (assessedAssignments.length > 0) {
            // Map ratings to numerical values
            const ratingValues = {
              'Excellent': 5,
              'Very Good': 4,
              'Good': 3,
              'Pass': 2,
              'Need to Repeat': 1
            };
            
            const assessmentSum = assessedAssignments.reduce((sum, assignment) => {
              return sum + (ratingValues[assignment.assessment.rating] || 0);
            }, 0);
            
            assessmentAvgValue = assessmentSum / assessedAssignments.length;
            
            // Determine text rating based on average
            if (assessmentAvgValue >= 4.5) assessmentText = 'Excellent';
            else if (assessmentAvgValue >= 3.5) assessmentText = 'Very Good';
            else if (assessmentAvgValue >= 2.5) assessmentText = 'Good';
            else if (assessmentAvgValue >= 1.5) assessmentText = 'Pass';
            else assessmentText = 'Need to Repeat';
          }

          // Update partial stats with assignment data
          setStats(prev => ({
            ...prev,
            assignmentCompletion: completionRate,
            assessmentAverage: { 
              value: assessmentAvgValue > 0 ? parseFloat(assessmentAvgValue.toFixed(1)) : 0, 
              text: assessmentText 
            }
          }));
        }

        // Process attendance data
        if (attendanceResponse.status === 'fulfilled') {
          const attendanceStats = attendanceResponse.value.data.stats;
          
          // Extract attendance rate from the stats
          if (attendanceStats && attendanceStats.attendanceRate) {
            const attendanceRate = parseInt(attendanceStats.attendanceRate.replace('%', '')) || 0;
            
            // Update stats with attendance rate
            setStats(prev => ({
              ...prev,
              attendanceRate: attendanceRate
            }));
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching child progress data:', err);
        setError('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProgress();
  }, [childId]);

  return { stats, loading, error };
};

// Child Card Component
const ChildCard = ({ child, onCardClick }) => {
  const firstLetter = child.name ? child.name.charAt(0).toUpperCase() : '?';
  const { stats, loading, error } = useChildProgress(child._id || child.id);
  
  return (
    <div className="child-card" onClick={() => onCardClick(child)}>
      <div className="child-info">
        <div className="avatar">
          <span>{firstLetter}</span>
        </div>
        <div className="child-details">
          <h3>{child.name || 'Unknown'}</h3>
        </div>
      </div>
      
      <div className="stats-container">
        <div className="stat-box assignment-stat">
          <div className="stat-value">{stats.assignmentCompletion}%</div>
          <div className="stat-label">Assignments</div>
        </div>
        
        <div className="stat-box rating-stat">
          <div className="stat-value">{stats.assessmentAverage.value}/5</div>
          <div className="stat-label">Avg. Rating</div>
        </div>
        
        <div className="stat-box attendance-stat">
          <div className="stat-value">{stats.attendanceRate}%</div>
          <div className="stat-label">Attendance</div>
        </div>
      </div>
    </div>
  );
};

const ParentDashboard = ({ user: propUser, data }) => {
  // Get user from context if not provided as prop
  const authContext = useContext(AuthContext);
  const contextUser = authContext?.user;
  const { logout } = authContext;
  const navigate = useNavigate();
  
  // Use either the prop user or context user
  const user = propUser || contextUser || { name: 'Parent' };
  
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch children data
  useEffect(() => {
    const fetchChildren = async () => {
      if (!user || !user.id) {
        console.log('ParentDashboard: No user ID available, cannot fetch children');
        setLoading(false);
        return;
      }
      
      console.log('ParentDashboard: Fetching children for parent ID:', user.id);
      setLoading(true);
      try {
        const response = await api.get(`/parents/children/${user.id}`);
        console.log('ParentDashboard: Children API response:', response.data);
        
        if (response.data.success && response.data.data.length > 0) {
          console.log(`ParentDashboard: Found ${response.data.data.length} children`);
          setChildren(response.data.data);
        } else {
          console.log('ParentDashboard: No children data returned from API');
          setChildren([]);
        }
      } catch (error) {
        console.error('ParentDashboard: Error fetching children:', error);
        setError('Failed to load children. Please try again.');
        setChildren([]);
      } finally {
        console.log('ParentDashboard: Finished loading children data');
        setLoading(false);
      }
    };
    
    fetchChildren();
  }, [user?.id]);
  
  // Handle click on child card
  const handleChildCardClick = (child) => {
    console.log('Clicked on child:', child.name);
    
    // Navigate to the Child Profile Page
    navigate(`/parent/children/${child._id || child.id}`, {
      state: {
        childId: child._id || child.id,
        childName: child.name
      }
    });
  };
  
  // Handle logout with confirmation
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-welcome">
        <h1>Welcome, {user.name || 'Parent'}</h1>
        <p>Select a child to view their progress</p>
      </div>
      
      <div className="dashboard-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading children data...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        ) : children.length > 0 ? (
          <div className="children-list">
            {children.map((child) => (
              <ChildCard 
                key={child._id || child.id} 
                child={child} 
                onCardClick={handleChildCardClick} 
              />
            ))}
          </div>
        ) : (
          <div className="content-card">
            <h2>Your Children</h2>
            <div className="empty-state">
              <p>No children linked to your account.</p>
              <p>Please contact your School or Centre Administrator.</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Logout button */}
      <div className="logout-container">
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};

export default ParentDashboard;